import { lazy, Suspense, useState, useEffect } from "react";

import { get as lockrGet, set as lockrSet } from "lockr";
import {
  BrowserRouter,
  useNavigate,
  useLocation,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import type { IIntakeAuthenticatedUser } from "utils/context";
import { RouterContext, TokenContext, IntakeTokenContext } from "utils/context";
import { Provider } from "react-redux";

import { setupStore } from "services/store";
import { fetchConstantsAndUpdateStore } from "services/sharedSlice.ts";
import { setLoggedInUser } from "reducers/main";
import { useAuthSession } from "hooks/useAuthSession";
import {
  setShowBackgroundJobs,
  setShowClaimLinkingDrawer,
  setShowClientDocumentsDrawer,
  setShowClientExportFeedsDrawer,
  setShowClientFeedsDrawer,
  setShowClientFilesDrawer,
  setShowClientInfoDrawer,
  setShowClinicianNotes,
  setShowDocumentsDrawer,
  setShowEmailDrawer,
  setShowICDCodingDrawer,
  setShowLegalNotes,
  setShowLienICDCodingDrawer,
  setShowLienPaymentHistoryDrawer,
  setShowMedicalHistory,
  setShowRelatedFilesDrawer,
  setShowRxHistory,
  setShowTasksDrawer,
} from "reducers/drawer";
import { NotFoundPage, RateLimitPage } from "pages/misc";

import ProtectedRoute from "components/ProtectedRoute.tsx";
import IntakeRoutes from "components/IntakeRoutes.tsx";
import { useToken } from "utils/context.ts";
import { useGetMeQuery } from "services/misc-api.ts";

import KaboodleApp from "components/KaboodleApp";
import LoadingIcon from "components/Loading/LoadingIcon";
import ClaimICDCodingFormWrapper from "pages/claims/ClaimICDCodingFormWrapper";
import ClaimAdditionalClaimants from "pages/claims/ClaimAdditionalClaimants";
import ClaimOverview from "pages/claims/ClaimOverview";
import ClaimMSA from "pages/claims/ClaimMSA";
import ClaimPlanInformation from "pages/claims/ClaimPlanInformation";
import ClaimQueryResponses from "pages/claims/ClaimQueryResponses.tsx";
import ClaimRepresentative from "pages/claims/ClaimRepresentative";
import ClaimCMP from "pages/claims/ClaimCMP";
import ClaimNotes from "pages/claims/ClaimNotes";
import KaboodleHotkeyWrapper from "components/KaboodleHotkeyWrapper";
import QuickBooksCallback from "components/Invoicing/QuickBooks/QuickBooksCallback";
import AnalyticsProvider from "components/AnalyticsProvider";
import { ReportActionsProvider } from "pages/finance/contexts/ReportActionsContext";

import {
  ClientTable,
  Login,
  KaboodleSwimlanes,
  ClaimsTablePage,
  CmsSubmissions,
  ClientForm,
  AuditHistory,
  ClaimantForm,
  ClaimantTable,
  ClaimsTransmissionHistory,
  FileTable,
  Section111Reporting,
  OpsReporting,
  Section111Analytics,
  InvoiceForm,
  SettingsPage,
  JobTable,
  AdjusterTable,
  AdjusterForm,
} from "pages/index";

import "styles/root.scss";

import { MSASectionPage, MSATable } from "pages/future-medical/msa";
import CostProjectionSectionPage from "pages/future-medical/cost-projection/CostProjectionSectionPage";
import AmendedReviewSectionPage from "pages/future-medical/amended-review-screening/AmendedReviewSectionPage";

import {
  RRETablePage,
  RREImportTablePage,
  RRETinsTable,
  RRETinPage,
  CmsCommunication,
  RREPoliciesTable,
  RREOverview,
} from "pages/rre";
import RREPolicyForm from "pages/rre/RREPolicyForm";
import RRETagsPage from "pages/rre/RRETagsPage";
import EditFile from "pages/files/EditFile.tsx";
import FileAuditHistory from "pages/files/FileAuditHistory.tsx";
import IntakeTable from "pages/files/IntakeTable.tsx";
import RecentlyViewedFiles from "pages/files/RecentlyViewedFiles.tsx";
import EmployerTable from "pages/employer/EmployerTable";
import EmployerForm from "pages/employer/EmployerForm";
import InsurerTable from "pages/insurer/InsurerTable";
import TPATable from "pages/tpa/TPATable";
import InsurerForm from "pages/insurer/InsurerForm";
import TPAForm from "pages/tpa/TPAForm";
// lazy load PreviewsContainer to split PDF and XLSX libraries into separate chunk
const PreviewsContainer = lazy(
  () => import("components/Documents/preview/PreviewsContainer"),
);
import PreviewErrorBoundary from "components/Documents/preview/PreviewErrorBoundary";

import ConsultingPage from "pages/consulting/ConsultingPage";
import FutureMedicalOverview from "pages/future-medical/FMOverview";
import CostProjectionTable from "pages/future-medical/cost-projection/CostProjectionTable";
import AmendedScreeningTable from "pages/future-medical/amended-review-screening/AmendedScreeningTable";
import LienOverview from "pages/liens/LienOverview";
import LiensSectionPage from "pages/liens/LiensSectionPage";
import LienInvoicePage from "pages/liens/LienInvoicePage";
import Section111InvoicingPage from "pages/client/Section111InvoicingPage";
import LoginIntake from "pages/intake/auth/LoginIntake";
import RegisterIntake from "pages/intake/auth/RegisterIntake";
import DashboardIntake from "pages/intake/DashboardIntake";
import ReferralSettingsPage from "pages/referral-settings/ReferralSettingsPage";
import LienDirectoryPage from "pages/liens/LienDirectoryPage";
import LienQueryPage from "pages/liens/LienQueryPage";
import LienNotesPage from "pages/liens/LienNotesPage";
import LienAuditHistory from "pages/liens/LienAuditHistory";
import DocumentManagementSectionPage from "pages/document-management/DocumentManagementSectionPage";
import ResetPasswordIntake from "pages/intake/auth/ResetPasswordIntake";
import ForgotPasswordIntake from "pages/intake/auth/ForgotPasswordIntake";
import FinancePage from "pages/finance/FinancePage";
import BulkDownloadPage from "pages/bulk-download/BulkDownloadPage";
import BulkDownloadsTable from "pages/downloads/BulkDownloadsTable";
import MetaDocPage from "pages/metadoc/MetaDocPage";
import { logToLogDna } from "utils/log-utils";
import { getSessionId } from "utils/sentry";
import { useSelector } from "utils/redux";
import { FEATURE_FLAGS } from "constants/FeatureFlags";
import MetadocProcessingToaster from "components/common/MetadocProcessingToaster";
import RatedAgeOCRWatcher from "components/common/RatedAgeOCRWatcher";
import {
  DEFAULT_ACCESS_TOKEN_REFRESH_INTERVAL_MS,
  DEFAULT_INACTIVITY_TIMEOUT_MS,
} from "constants/AuthConstants";

const store = setupStore();

type IRouterContext = {
  location: ReturnType<typeof useLocation>;
  navigate: ReturnType<typeof useNavigate>;
};

// wrapper component to check feature flags for routes
function FeatureFlaggedRoute({ children }: { children: React.ReactNode }) {
  const constants = useSelector((state) => state.shared.constants);
  const isMetaDocEnabled =
    constants?.FEATURE_FLAGS?.[FEATURE_FLAGS.METADOC_ENABLED];

  return (
    <ProtectedRoute isAllowed={isMetaDocEnabled} redirectPath="/404">
      {children}
    </ProtectedRoute>
  );
}

export function KaboodleRootWrapper() {
  const navigate = useNavigate();
  const location = useLocation();
  const { accessToken } = useToken();
  const { data: me } = useGetMeQuery(undefined, {
    skip: !accessToken,
  });
  const uiSessionId = getSessionId();
  const userId = me?.id || "unknown";
  const userEmail = me?.email || "unknown";

  useEffect(() => {
    logToLogDna(
      "info",
      `Path: ${window.location.pathname}, User Id: ${userId}, UI Session ID: ${uiSessionId}`,
      { uiSessionId, userId, userEmail },
    );
  }, [uiSessionId, userId, userEmail]);

  // setup global navigation functions for middleware
  useEffect(() => {
    // make the navigation functions available globally for error middleware
    window.navigate404 = (path = "/404") => {
      navigate(path);
    };
    window.navigateRateLimit = () => {
      navigate("/rate-limit");
    };
    return () => {
      delete window.navigate404;
      delete window.navigateRateLimit;
    };
  }, [navigate]);

  useEffect(() => {
    store.dispatch(setLoggedInUser(me));
  }, [me]);

  useEffect(() => {
    if (accessToken) {
      store.dispatch(fetchConstantsAndUpdateStore({ accessToken }));
    }
  }, [accessToken]);

  // handle drawer URL parameter
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const drawerParam = params.get("drawer");

    if (drawerParam) {
      // map drawer parameter to Redux action
      const drawerParamMap: Record<string, () => void> = {
        documents: () => store.dispatch(setShowDocumentsDrawer(true)),
        tasks: () => store.dispatch(setShowTasksDrawer(true)),
        medicalHistory: () => store.dispatch(setShowMedicalHistory(true)),
        rxHistory: () => store.dispatch(setShowRxHistory(true)),
        legalNotes: () => store.dispatch(setShowLegalNotes(true)),
        clinicianNotes: () => store.dispatch(setShowClinicianNotes(true)),
        email: () => store.dispatch(setShowEmailDrawer(true)),
        relatedFiles: () => store.dispatch(setShowRelatedFilesDrawer(true)),
        claimLinking: () => store.dispatch(setShowClaimLinkingDrawer(true)),
        icdCoding: () => store.dispatch(setShowICDCodingDrawer(true)),
        lienICDCoding: () => store.dispatch(setShowLienICDCodingDrawer(true)),
        lienPaymentHistory: () =>
          store.dispatch(setShowLienPaymentHistoryDrawer(true)),
        backgroundJobs: () => store.dispatch(setShowBackgroundJobs(true)),
        clientInfo: () => store.dispatch(setShowClientInfoDrawer(true)),
        clientFiles: () => store.dispatch(setShowClientFilesDrawer(true)),
        clientDocuments: () =>
          store.dispatch(setShowClientDocumentsDrawer(true)),
        clientFeeds: () => store.dispatch(setShowClientFeedsDrawer(true)),
        clientExportFeeds: () =>
          store.dispatch(setShowClientExportFeedsDrawer(true)),
      };

      const action = drawerParamMap[drawerParam];
      if (action) {
        action();

        // remove drawer parameter from URL
        params.delete("drawer");
        const newSearch = params.toString();
        const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ""}`;
        navigate(newUrl, { replace: true });
      }
    }
  }, [location.search, navigate]);

  return (
    <RouterContext.Provider value={{ location, navigate } as IRouterContext}>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<Login />} />
        <Route
          path="/intake/:organizationSlug/auth/register"
          element={<RegisterIntake />}
        />
        <Route
          path="/intake/:organizationSlug/auth/login"
          element={<LoginIntake />}
        />
        <Route
          path="/intake/:organizationSlug/auth/forgot-password"
          element={<ForgotPasswordIntake />}
        />
        <Route
          path="/intake/:organizationSlug/auth/reset-password"
          element={<ResetPasswordIntake />}
        />

        {/* Intake dashboard routes - slug-based */}
        <Route
          path="/intake/:organizationSlug/dashboard"
          element={
            <IntakeRoutes>
              <DashboardIntake />
            </IntakeRoutes>
          }
        />

        {/* QuickBooks callback route */}
        <Route
          path="/quickbooks/callback"
          element={
            <ProtectedRoute>
              <QuickBooksCallback />
            </ProtectedRoute>
          }
        />

        {/* Protected routes using nested routes */}
        <Route element={<ProtectedRoute />}>
          {/* Root routes */}
          <Route
            path="/your-tasks"
            element={<KaboodleApp children={<KaboodleSwimlanes />} />}
          />
          <Route
            path="/"
            element={<KaboodleApp children={<KaboodleSwimlanes />} />}
          />

          {/* Section 111 routes */}
          <Route
            path="/section-111"
            element={<Navigate to="/section-111/claims" />}
          />
          <Route
            path="/section-111/claims"
            element={
              <KaboodleApp classNames="table" children={<ClaimsTablePage />} />
            }
          />
          <Route
            path="/section-111/claims/:claimId/overview"
            element={<KaboodleApp children={<ClaimOverview />} />}
          />
          <Route
            path="/section-111/claims/:claimId/audit-history"
            element={<KaboodleApp children={<AuditHistory />} />}
          />
          <Route
            path="/section-111/claims/:claimId/transmission-history"
            element={
              <KaboodleApp
                classNames="table"
                children={<ClaimsTransmissionHistory />}
              />
            }
          />
          <Route
            path="/section-111/claims/:claimId/icd-coding"
            element={<KaboodleApp children={<ClaimICDCodingFormWrapper />} />}
          />
          <Route
            path="/section-111/claims/:claimId/additional-claimants"
            element={<KaboodleApp children={<ClaimAdditionalClaimants />} />}
          />
          <Route
            path="/section-111/claims/:claimId/msa"
            element={<KaboodleApp children={<ClaimMSA />} />}
          />
          <Route
            path="/section-111/claims/:claimId/plan-information"
            element={<KaboodleApp children={<ClaimPlanInformation />} />}
          />
          <Route
            path="/section-111/claims/:claimId/query-response"
            element={<KaboodleApp children={<ClaimQueryResponses />} />}
          />
          <Route
            path="/section-111/claims/:claimId/representative"
            element={<KaboodleApp children={<ClaimRepresentative />} />}
          />
          <Route
            path="/section-111/claims/:claimId/cmp"
            element={<KaboodleApp children={<ClaimCMP />} />}
          />
          <Route
            path="/section-111/claims/:claimId/notes"
            element={<KaboodleApp children={<ClaimNotes />} />}
          />
          <Route
            path="/section-111/rres"
            element={
              <KaboodleApp classNames="table" children={<RRETablePage />} />
            }
          />
          <Route
            path="/section-111/rres/:rreId/overview"
            element={<KaboodleApp children={<RREOverview />} />}
          />
          <Route
            path="/section-111/rres/:rreId/cms-submissions"
            element={<KaboodleApp children={<CmsSubmissions />} />}
          />
          <Route
            path="/section-111/rres/:rreId/imports"
            element={
              <KaboodleApp
                classNames="table"
                children={<RREImportTablePage />}
              />
            }
          />
          <Route
            path="/section-111/rres/:rreId/tins"
            element={<KaboodleApp children={<RRETinsTable />} />}
          />
          <Route
            path="/section-111/rres/:rreId/tags"
            element={<KaboodleApp children={<RRETagsPage />} />}
          />
          <Route
            path="/section-111/rres/:rreId/cms-communication"
            element={<KaboodleApp children={<CmsCommunication />} />}
          />
          <Route
            path="/section-111/rres/:rreId/policies"
            element={
              <KaboodleApp classNames="table" children={<RREPoliciesTable />} />
            }
          />
          <Route
            path="/section-111/reporting"
            element={<KaboodleApp children={<Section111Reporting />} />}
          />
          <Route
            path="/section-111/dashboard"
            element={<KaboodleApp children={<Section111Analytics />} />}
          />
          <Route
            path="/section-111/rres/:rreId/tins/:rreTinId/overview"
            element={<KaboodleApp children={<RRETinPage />} />}
          />
          <Route
            path="/section-111/rres/:rreId/policies/:policyId/overview"
            element={<KaboodleApp children={<RREPolicyForm />} />}
          />

          {/* Lien routes */}
          <Route
            path="/liens/:lienFileId/metadoc"
            element={
              <FeatureFlaggedRoute>
                <KaboodleApp children={<MetaDocPage />} />
              </FeatureFlaggedRoute>
            }
          />
          <Route
            path="/liens/:lienFileId/overview/"
            element={<KaboodleApp children={<LienOverview />} />}
          />
          <Route
            path="/liens/:lienFileId/query"
            element={<KaboodleApp children={<LienQueryPage />} />}
          />
          <Route
            path="/liens/:lienFileId/lien/:lienId"
            element={<KaboodleApp children={<LiensSectionPage />} />}
          />
          <Route
            path="/liens/:lienFileId/lien/:lienId/:lienSection"
            element={<KaboodleApp children={<LiensSectionPage />} />}
          />
          <Route
            path="/liens/:lienFileId/lien/:lienId/audit-history"
            element={<KaboodleApp children={<LienAuditHistory />} />}
          />
          <Route
            path="/liens/:lienFileId/invoice"
            element={<KaboodleApp children={<LienInvoicePage />} />}
          />
          <Route
            path="/liens/:lienFileId/directory"
            element={<KaboodleApp children={<LienDirectoryPage />} />}
          />
          <Route
            path="/liens/:lienFileId/notes"
            element={<KaboodleApp children={<LienNotesPage />} />}
          />

          {/* Future Medical Metadoc routes */}
          <Route
            path="/future-medical/msa/:msaId/metadoc"
            element={
              <FeatureFlaggedRoute>
                <KaboodleApp children={<MetaDocPage />} />
              </FeatureFlaggedRoute>
            }
          />
          <Route
            path="/future-medical/msa/:msaId/metadoc/:metadocId"
            element={
              <FeatureFlaggedRoute>
                <KaboodleApp children={<MetaDocPage />} />
              </FeatureFlaggedRoute>
            }
          />
          <Route
            path="/future-medical/amended-review/:msaId/metadoc"
            element={
              <FeatureFlaggedRoute>
                <KaboodleApp children={<MetaDocPage />} />
              </FeatureFlaggedRoute>
            }
          />
          <Route
            path="/future-medical/amended-review/:msaId/metadoc/:metadocId"
            element={
              <FeatureFlaggedRoute>
                <KaboodleApp children={<MetaDocPage />} />
              </FeatureFlaggedRoute>
            }
          />
          <Route
            path="/future-medical/mcp/:mcpId/metadoc"
            element={
              <FeatureFlaggedRoute>
                <KaboodleApp children={<MetaDocPage />} />
              </FeatureFlaggedRoute>
            }
          />
          <Route
            path="/future-medical/mcp/:mcpId/metadoc/:metadocId"
            element={
              <FeatureFlaggedRoute>
                <KaboodleApp children={<MetaDocPage />} />
              </FeatureFlaggedRoute>
            }
          />
          <Route
            path="/future-medical/lcp/:lcpId/metadoc"
            element={
              <FeatureFlaggedRoute>
                <KaboodleApp children={<MetaDocPage />} />
              </FeatureFlaggedRoute>
            }
          />
          <Route
            path="/future-medical/lcp/:lcpId/metadoc/:metadocId"
            element={
              <FeatureFlaggedRoute>
                <KaboodleApp children={<MetaDocPage />} />
              </FeatureFlaggedRoute>
            }
          />
          <Route
            path="/future-medical/ar-screening/:reviewId/metadoc"
            element={
              <FeatureFlaggedRoute>
                <KaboodleApp children={<MetaDocPage />} />
              </FeatureFlaggedRoute>
            }
          />
          <Route
            path="/future-medical/ar-screening/:reviewId/metadoc/:metadocId"
            element={
              <FeatureFlaggedRoute>
                <KaboodleApp children={<MetaDocPage />} />
              </FeatureFlaggedRoute>
            }
          />
          <Route
            path="/future-medical"
            element={<Navigate to="/future-medical/msa" />}
          />
          <Route
            path="/future-medical/overview"
            element={<KaboodleApp children={<FutureMedicalOverview />} />}
          />
          <Route
            path="/future-medical/msa"
            element={
              <KaboodleApp
                classNames="table"
                children={<MSATable serviceType="MSA" />}
              />
            }
          />
          <Route
            path="/future-medical/msa/:msaId/:msaSection"
            element={<KaboodleApp children={<MSASectionPage />} />}
          />

          {/* Amended Review routes */}
          <Route
            path="/future-medical/amended-review"
            element={
              <KaboodleApp
                classNames="table"
                children={<MSATable serviceType="ARV" />}
              />
            }
          />
          <Route
            path="/future-medical/amended-review/:msaId"
            element={<KaboodleApp children={<MSASectionPage />} />}
          />
          <Route
            path="/future-medical/amended-review/:msaId/:msaSection"
            element={<KaboodleApp children={<MSASectionPage />} />}
          />

          {/* AR Screening routes */}
          <Route
            path="/future-medical/ar-screening"
            element={
              <KaboodleApp
                classNames="table"
                children={<AmendedScreeningTable />}
              />
            }
          />
          <Route
            path="/future-medical/ar-screening/:reviewId"
            element={<KaboodleApp children={<AmendedReviewSectionPage />} />}
          />
          <Route
            path="/future-medical/ar-screening/:reviewId/:section"
            element={<KaboodleApp children={<AmendedReviewSectionPage />} />}
          />

          {/* Cost Projection routes */}
          <Route
            path="/future-medical/mcp"
            element={
              <KaboodleApp
                classNames="table"
                children={<CostProjectionTable serviceType="mcp" />}
              />
            }
          />
          <Route
            path="/future-medical/lcp"
            element={
              <KaboodleApp
                classNames="table"
                children={<CostProjectionTable serviceType="lcp" />}
              />
            }
          />

          {/* LCP Detail Routes */}
          <Route
            path="/future-medical/lcp/:costProjectionId/:costProjectionSection"
            element={<KaboodleApp children={<CostProjectionSectionPage />} />}
          />
          <Route
            path="/future-medical/lcp/:costProjectionId"
            element={<KaboodleApp children={<CostProjectionSectionPage />} />}
          />

          {/* MCP Detail Routes */}
          <Route
            path="/future-medical/mcp/:costProjectionId/:costProjectionSection"
            element={<KaboodleApp children={<CostProjectionSectionPage />} />}
          />
          <Route
            path="/future-medical/mcp/:costProjectionId"
            element={<KaboodleApp children={<CostProjectionSectionPage />} />}
          />

          {/* Document Management routes */}
          <Route
            path="/document-management"
            element={
              <Navigate to="/document-management/:documentManagementFileId/operations" />
            }
          />
          <Route
            path="/document-management/:documentManagementFileId/:dmSection"
            element={
              <KaboodleApp children={<DocumentManagementSectionPage />} />
            }
          />
          <Route
            path="/document-management/:documentManagementFileId/metadoc"
            element={
              <FeatureFlaggedRoute>
                <KaboodleApp children={<MetaDocPage />} />
              </FeatureFlaggedRoute>
            }
          />
          <Route
            path="/document-management/:documentManagementFileId/metadoc/:metadocId"
            element={
              <FeatureFlaggedRoute>
                <KaboodleApp children={<MetaDocPage />} />
              </FeatureFlaggedRoute>
            }
          />

          {/* Operations routes */}
          <Route
            path="/operations"
            element={<Navigate to="/operations/files" />}
          />
          <Route
            path="/operations/files/:fileId/metadoc"
            element={
              <FeatureFlaggedRoute>
                <KaboodleApp children={<MetaDocPage />} />
              </FeatureFlaggedRoute>
            }
          />
          <Route
            path="/operations/files/:fileId/metadoc/:metadocId"
            element={
              <FeatureFlaggedRoute>
                <KaboodleApp children={<MetaDocPage />} />
              </FeatureFlaggedRoute>
            }
          />
          <Route
            path="/operations/files/:fileId/audit-history"
            element={<KaboodleApp children={<FileAuditHistory />} />}
          />
          <Route
            path="/operations/clients"
            element={
              <KaboodleApp classNames="table" children={<ClientTable />} />
            }
          />
          <Route
            path="/operations/clients/:clientId/overview"
            element={<KaboodleApp children={<ClientForm />} />}
          />
          <Route
            path="/operations/clients/:clientId/invoicing"
            element={<KaboodleApp children={<InvoiceForm />} />}
          />
          <Route
            path="/operations/clients/:clientId/client-invoicing"
            element={<KaboodleApp children={<Section111InvoicingPage />} />}
          />
          <Route
            path="/consulting/:miscServiceId"
            element={<KaboodleApp children={<ConsultingPage />} />}
          />
          <Route
            path="/audit/:miscServiceId"
            element={<KaboodleApp children={<ConsultingPage />} />}
          />
          <Route
            path="/section-111-general/:miscServiceId"
            element={<KaboodleApp children={<ConsultingPage />} />}
          />
          <Route
            path="/software-license/:miscServiceId"
            element={<KaboodleApp children={<ConsultingPage />} />}
          />
          <Route
            path="/operations/claimants"
            element={
              <KaboodleApp classNames="table" children={<ClaimantTable />} />
            }
          />
          <Route
            path="/operations/claimants/:claimantId/overview"
            element={<KaboodleApp children={<ClaimantForm />} />}
          />
          <Route
            path="/operations/employers"
            element={
              <KaboodleApp classNames="table" children={<EmployerTable />} />
            }
          />
          <Route
            path="/operations/insurers"
            element={
              <KaboodleApp classNames="table" children={<InsurerTable />} />
            }
          />
          <Route
            path="/operations/tpas"
            element={<KaboodleApp classNames="table" children={<TPATable />} />}
          />
          <Route
            path="/operations/adjusters"
            element={
              <KaboodleApp classNames="table" children={<AdjusterTable />} />
            }
          />
          <Route
            path="/operations/employers/:employerId/overview"
            element={<KaboodleApp children={<EmployerForm />} />}
          />
          <Route
            path="/operations/insurers/:insurerId/overview"
            element={<KaboodleApp children={<InsurerForm />} />}
          />
          <Route
            path="/operations/tpas/:tpaId/overview"
            element={<KaboodleApp children={<TPAForm />} />}
          />
          <Route
            path="/operations/adjusters/:adjusterId/overview"
            element={<KaboodleApp children={<AdjusterForm />} />}
          />
          <Route
            path="/operations/files/:fileId/edit"
            element={<Navigate to="claimant-information" replace />}
          />
          <Route
            path="/operations/files/:fileId/edit/:tab"
            element={<KaboodleApp children={<EditFile />} />}
          />
          <Route
            path="/operations/files"
            element={<KaboodleApp children={<FileTable />} />}
          />
          <Route
            path="/operations/recently-viewed"
            element={<KaboodleApp children={<RecentlyViewedFiles />} />}
          />
          <Route
            path="/operations/intake"
            element={<KaboodleApp children={<IntakeTable />} />}
          />
          <Route
            path="/previews"
            element={
              <KaboodleApp
                children={
                  <Suspense fallback={<LoadingIcon />}>
                    <PreviewErrorBoundary>
                      <PreviewsContainer />
                    </PreviewErrorBoundary>
                  </Suspense>
                }
              />
            }
          />
          <Route
            path="/settings"
            element={<KaboodleApp children={<SettingsPage />} />}
          />
          <Route
            path="/referral-settings"
            element={<KaboodleApp children={<ReferralSettingsPage />} />}
          />
          <Route
            path="/jobs"
            element={<KaboodleApp children={<JobTable />} />}
          />
          <Route
            path="/reporting"
            element={<Navigate to="/reporting/finance" />}
          />
          <Route
            path="/reporting/finance"
            element={<KaboodleApp children={<FinancePage />} />}
          />
          <Route
            path="/reporting/operations"
            element={<KaboodleApp children={<OpsReporting />} />}
          />
          <Route
            path="/bulk-download/:requestId"
            element={<KaboodleApp children={<BulkDownloadPage />} />}
          />
          <Route
            path="/downloads"
            element={<KaboodleApp children={<BulkDownloadsTable />} />}
          />
        </Route>

        {/* Rate limit page */}
        <Route path="/rate-limit" element={<RateLimitPage />} />

        {/* Catch-all route */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </RouterContext.Provider>
  );
}

const KaboodleRootContent = () => {
  const [intakeToken, setIntakeToken] = useState<string | null>(null);
  const setIntakeTokenWithLocalStorage = (token: string | null) => {
    setIntakeToken(token);
    lockrSet("intakeToken", token);
  };

  // The externally-authenticated intake user (referring-party login flow) --
  // persisted the same way as intakeToken so it survives page reloads.
  const [intakeUser, setIntakeUser] = useState<IIntakeAuthenticatedUser | null>(
    null,
  );
  const setIntakeUserWithLocalStorage = (
    user: IIntakeAuthenticatedUser | null,
  ) => {
    setIntakeUser(user);
    lockrSet("intakeUser", user);
  };

  // auth session management - handles token refresh, idle logout, visibility change
  const { accessToken } = useAuthSession({
    store,
    idleTimeoutMs: DEFAULT_INACTIVITY_TIMEOUT_MS, // 20 minutes
    refreshIntervalMs: DEFAULT_ACCESS_TOKEN_REFRESH_INTERVAL_MS, // 19 min 10 sec
  });

  return (
    <TokenContext.Provider value={{ accessToken: accessToken }}>
      <IntakeTokenContext.Provider
        value={{
          intakeToken: lockrGet("intakeToken") || intakeToken,
          setIntakeToken: setIntakeTokenWithLocalStorage,
          intakeUser: lockrGet("intakeUser") || intakeUser,
          setIntakeUser: setIntakeUserWithLocalStorage,
        }}
      >
        <BrowserRouter>
          <AnalyticsProvider>
            <ReportActionsProvider>
              <KaboodleHotkeyWrapper>
                <KaboodleRootWrapper />
                <MetadocProcessingToaster />
                <RatedAgeOCRWatcher />
              </KaboodleHotkeyWrapper>
            </ReportActionsProvider>
          </AnalyticsProvider>
        </BrowserRouter>
      </IntakeTokenContext.Provider>
    </TokenContext.Provider>
  );
};

const KaboodleRoot = () => {
  return (
    <Provider store={store}>
      <KaboodleRootContent />
    </Provider>
  );
};

export default KaboodleRoot;
